/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import type { Moment } from 'moment'

/**
 * Workflow view types matching legacy WorkflowViewType
 */
export enum WorkflowViewType {
  ACTIVE = 'active',
  FINISHED = 'finished',
  ALL = 'all'
}

/**
 * Workflow initiator interface
 */
export interface WorkflowInitiator {
  id: number
  name: string
}

/**
 * Workflow flags indicating available actions
 */
export interface WorkflowFlags {
  canViewComments: boolean
  canResume: boolean
  canCancel: boolean
  canSuspend: boolean
  canUpdateEffectiveDate: boolean
  canEdit: boolean
  canAddCollaborators: boolean
  canModifyReviewers: boolean
  canModifyApprovers: boolean
  canModifyTrainees: boolean
  canModifyRejectionReviewers: boolean
}

/**
 * Study reference for EOS workflows
 */
export interface WorkflowStudy {
  name: string
  id?: number
}

/**
 * Site reference for EOS workflows
 */
export interface WorkflowSite {
  name: string
  id?: number
}

/**
 * ETMF document details
 */
export interface FilteredFileToEtmfDocDetails {
  study?: string
  country?: string
  site?: string
}

/**
 * Main Workflow data interface
 */
export interface Workflow {
  id: number
  formattedId: string
  type: string
  localizedType: string
  status: string
  progress: number
  currentPhase: string
  startDate: number
  initiator: WorkflowInitiator
  rejected: boolean
  flags: WorkflowFlags
  study?: WorkflowStudy
  site?: WorkflowSite
  filteredFileToEtmfDocDetails?: FilteredFileToEtmfDocDetails
  [key: string]: any
}

/**
 * Filter data hierarchy for ETMF
 */
export interface FilterDataHierarchy {
  product: any | null
  study: any | null
  environment: any | null
  country: any | null
  site: any | null
}

/**
 * Filter form values interface
 */
export interface WorkflowsFilterValues {
  workflowType: string[]
  workflowPhase: string[]
  assignedTo: number[]
  initiatorId: number[]
  id: Array<{ value: number; label: string }>
  startFrom: Moment | null
  startTo: Moment | null
  study: number | null
  site: number | null
  isEosSelected: boolean
  isEosEnabled: boolean
  isEtmfEnabled: boolean
  data: FilterDataHierarchy
  folderId: { id: number; name: string } | null
  documentTypeTemplateId: number | null
}

/**
 * API filter parameters after transformation
 */
export interface WorkflowsApiFilter {
  workflowType?: string[]
  workflowPhase?: string[]
  assignedTo?: number[]
  initiatorId?: number[]
  id?: number[]
  startFrom?: string
  startTo?: string
  studyFolderId?: number
  siteFolderId?: number
  fsiIds?: number[]
  folderId?: number
  documentTypeTemplateId?: number
  [key: string]: any
}

/**
 * Table menu options for multi-selection
 */
export interface TableMenuOptions {
  selectedIds?: number[]
  selectedWholeResult?: boolean
  selectedItems?: Workflow[]
  deselectAll?: () => void
  meta?: any
}

/**
 * Select option interface for dropdowns
 */
export interface SelectOption {
  value: any
  label: string
}

/**
 * Filter options for dropdowns
 */
export interface WorkflowsFilterOptions {
  availableUsers: SelectOption[]
  availableStudies: SelectOption[]
  availableSites: SelectOption[]
  availableWorkflowTypes: SelectOption[]
  availableWorkflowPhases: SelectOption[]
  product: SelectOption[]
  study: SelectOption[]
  environment: SelectOption[]
  country: SelectOption[]
  site: SelectOption[]
}
